import {
    type ChangeEvent,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import LoginActions from "./LoginActions";
import LoginPassword from "./LoginPassword";

interface Props {
    password: string;
    username: string;
    showUsername?: boolean;
    touchNumbers: boolean;
    requirePassword: boolean;
    onChange: (name: string, value: string) => void;
    onLogin: () => Promise<void>;
    onClock: () => Promise<void>;
}

export interface LoginFormRef {
    focus: (select?: boolean) => void;
}

export const LoginForm = forwardRef<LoginFormRef, Props>((props, ref) => {
    const passwordRef = useRef<LoginPassword>(null);

    const [ loading, setLoading ] = useState(false);
    const focusAfterLoading = useRef(false);

    useImperativeHandle(ref, () => ({
        focus: (select = false) => {
            passwordRef.current?.focus(select);
        },
    }), []);

    useEffect(() => {
        if(focusAfterLoading.current && passwordRef.current && !loading) {
            focusAfterLoading.current = false;
            passwordRef.current?.focus(true);
        }
    }, [ loading ]);

    const onLogin = () => {
        setLoading(true);

        props.onLogin()
            .then(() => {
                setLoading(false);
            })
            .catch(err => {
                if(err !== "Failed to login") {
                    if(props.requirePassword) {
                        props.onChange("password", "");
                    } else {
                        props.onChange("username", "");
                    }
                }

                setLoading(false);
                focusAfterLoading.current = true;
            });
    };

    const onClock = () => {
        // TODO: Implement this?
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.currentTarget.value;
        let name = e.currentTarget.name;

        switch(name) {
            case "shopfront-field-u":
                name = "username";
                break;
            case "shopfront-field-p":
                name = "password";
                break;
        }

        props.onChange(name, value);
    };

    return (
        <div className="login-form">
            <LoginPassword
                ref={passwordRef}
                username={props.username}
                password={props.password}
                requirePassword={props.requirePassword}
                showUsername={!!props.showUsername}
                touchNumbers={props.touchNumbers}
                onChange={onChange}
                onLogin={onLogin}
                disabled={loading}
            />
            <LoginActions
                onLogin={onLogin}
                onClock={onClock}
                loadingLogin={loading}
                loadingClock={false}
            />
        </div>
    );
});
LoginForm.displayName = "LoginForm";
